import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  Button,
  Dimmer,
  Header,
  Icon,
  Input,
  Loader,
  Message,
  Modal,
  Segment,
  Table,
  Label,
} from "semantic-ui-react";

import moment from "moment";
import { PDFViewer } from "@react-pdf/renderer";

import {
  collection,
  getDocs,
  query,
  where,
} from "firebase/firestore";

import { db } from "../firebase/firebaseConfig";
import { UserContext } from "../context/UserContext";

// ======================================================
// PDF PREVENTIVA
// ======================================================
import ReporteMantePrevPDF from "./ReporteMantePrevPDF";

// ======================================================
// PDF CORRECTIVA
// ======================================================
import ReporteFinalPresuPDF from "./ReporteFinalPresuPDF";


// ======================================================
// ESTILOS
// ======================================================

const COLORS = {
  background: "#f8fafc",
  card: "#ffffff",
  border: "#e5e9ef",
  title: "#172b4d",
  subtitle: "#6b778c",

  teal: "#0d9da5",
  tealSoft: "#e9f7f8",

  blue: "#2185d0",
  blueSoft: "#eef6fc",

  green: "#21ba45",
  greenSoft: "#eefaf1",

  orange: "#f2711c",
  orangeSoft: "#fff4ec",

  red: "#db2828",
  redSoft: "#fff1f0",

  greySoft: "#f4f5f7",
};


const stickyTh = {
  position: "sticky",
  top: 0,
  zIndex: 2,
  background: "#f8fafc",
  color: "#42526e",
  fontSize: 12,
  fontWeight: 700,
  whiteSpace: "nowrap",
  borderBottom: `1px solid ${COLORS.border}`,
};


const cardStyle = {
  background: COLORS.card,
  border: `1px solid ${COLORS.border}`,
  borderRadius: 8,
  boxShadow: "0 1px 3px rgba(9, 30, 66, 0.08)",
};


const normalCellStyle = {
  fontSize: 12,
  color: "#344563",
};


// ======================================================
// HELPERS
// ======================================================

const safeText = (value) => {
  if (
    value === null ||
    value === undefined
  ) {
    return "";
  }

  if (value?.toDate) {
    return moment(
      value.toDate()
    ).format(
      "DD/MM/YYYY HH:mm"
    );
  }

  if (value instanceof Date) {
    return moment(value).format(
      "DD/MM/YYYY HH:mm"
    );
  }

  if (
    typeof value === "object"
  ) {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }

  return String(value);
};


const obtenerMillis = (fecha) => {
  if (!fecha) {
    return 0;
  }

  if (fecha?.toMillis) {
    return fecha.toMillis();
  }

  if (fecha?.toDate) {
    return fecha.toDate().getTime();
  }

  if (fecha instanceof Date) {
    return fecha.getTime();
  }

  if (fecha?.seconds) {
    return fecha.seconds * 1000;
  }

  const parsed = new Date(fecha);

  if (!Number.isNaN(parsed.getTime())) {
    return parsed.getTime();
  }

  return 0;
};


const formatearFecha = (fecha) => {
  const millis = obtenerMillis(fecha);

  if (!millis) {
    return "-";
  }

  return moment(millis).format(
    "DD/MM/YYYY HH:mm"
  );
};


// Firestore permite una cantidad limitada de valores
// dentro de where("campo", "in", [...]).
const dividirArray = (
  arr,
  size = 25
) => {
  const grupos = [];

  for (
    let i = 0;
    i < arr.length;
    i += size
  ) {
    grupos.push(
      arr.slice(
        i,
        i + size
      )
    );
  }

  return grupos;
};


// ======================================================
// COMPONENTE
// ======================================================

const ReporteMantencionesContrato = () => {

  const { users } =
    useContext(UserContext);


  // ====================================================
  // EMPRESA SERVICIO TÉCNICO INTERNO
  // ====================================================

  const empidst =
    process.env
      .REACT_APP_EMPID_STINTERNO;


  // ====================================================
  // DATOS PRINCIPALES
  // ====================================================

  const [
    registros,
    setRegistros,
  ] = useState([]);


  const [
    usuariosST,
    setUsuariosST,
  ] = useState([]);


  // ====================================================
  // UI
  // ====================================================

  const [
    loading,
    setLoading,
  ] = useState(true);


  const [
    errorCarga,
    setErrorCarga,
  ] = useState("");


  const [
    buscador,
    setBuscador,
  ] = useState("");


  const [
    filtroAtencion,
    setFiltroAtencion,
  ] = useState("TODOS");


  // ====================================================
  // PDF
  // ====================================================

  const [
    openPdf,
    setOpenPdf,
  ] = useState(false);


  const [
    pdfLoading,
    setPdfLoading,
  ] = useState(false);


  const [
    tipoPdf,
    setTipoPdf,
  ] = useState("");


  const [
    cabecera,
    setCabecera,
  ] = useState([]);


  const [
    itemsInst,
    setItemsInst,
  ] = useState([]);


  const [
    itemsCheck,
    setItemsCheck,
  ] = useState([]);


  const [
    itemsMed,
    setItemsMed,
  ] = useState([]);


  const [
    itemsSeg,
    setItemsSeg,
  ] = useState([]);


  const [
    obs,
    setObs,
  ] = useState("");


  const [
    userAdd,
    setUserAdd,
  ] = useState([]);


  // ====================================================
  // DATOS PRESUPUESTO / CORRECTIVA
  // ====================================================

  const [
    presu,
    setPresu,
  ] = useState([]);


  const [
    falla,
    setFalla,
  ] = useState([]);


  const [
    total,
    setTotal,
  ] = useState(0);


  // ====================================================
  // LIMPIAR PDF
  // ====================================================

  const limpiarDatosPdf =
    useCallback(() => {

      setTipoPdf("");

      setCabecera([]);

      setItemsInst([]);
      setItemsCheck([]);
      setItemsMed([]);
      setItemsSeg([]);

      setPresu([]);
      setFalla([]);
      setTotal(0);

      setObs("");
      setUserAdd([]);

    }, []);


  // ====================================================
  // CARGAR USUARIOS DEL ST
  // ====================================================

  const cargarUsuariosST =
    useCallback(
      async () => {

        if (!empidst) {
          return;
        }

        const qUsuarios =
          query(
            collection(
              db,
              "usuarios"
            ),
            where(
              "emp_id",
              "==",
              empidst
            )
          );


        const snap =
          await getDocs(
            qUsuarios
          );


        const data =
          snap.docs.map(
            (d) => ({
              id: d.id,
              ...d.data(),
            })
          );


        setUsuariosST(data);

      },
      [
        empidst,
      ]
    );


  // ====================================================
  // SELECCIONAR MEJOR CABECERA DE BITÁCORA
  // ====================================================

  const crearMapaBitacoras =
    useCallback(
      (
        bitacoras = []
      ) => {

        const mapa =
          new Map();


        bitacoras.forEach(
          (bitacora) => {

            if (
              !bitacora.id_ingreso
            ) {
              return;
            }


            const actual =
              mapa.get(
                bitacora.id_ingreso
              );


            if (!actual) {

              mapa.set(
                bitacora.id_ingreso,
                bitacora
              );

              return;
            }


            const actualConfirmada =
              actual.confirmado === true;


            const nuevaConfirmada =
              bitacora.confirmado === true;


            // Prioridad a bitácora confirmada.
            if (
              nuevaConfirmada &&
              !actualConfirmada
            ) {

              mapa.set(
                bitacora.id_ingreso,
                bitacora
              );

              return;
            }


            // Si ambas están en la misma condición,
            // dejamos la más reciente.
            if (
              nuevaConfirmada ===
              actualConfirmada
            ) {

              const fechaActual =
                obtenerMillis(
                  actual.fechamod ||
                  actual.fechaadd
                );


              const fechaNueva =
                obtenerMillis(
                  bitacora.fechamod ||
                  bitacora.fechaadd
                );


              if (
                fechaNueva >
                fechaActual
              ) {

                mapa.set(
                  bitacora.id_ingreso,
                  bitacora
                );
              }
            }

          }
        );


        return mapa;

      },
      []
    );


  // ====================================================
  // BUSCAR CABECERAS DE BITÁCORA POR GRUPOS
  // ====================================================

  const buscarBitacorasCab =
    useCallback(
      async ({
        coleccion,
        idsIngresos,
      }) => {

        if (
          !empidst ||
          !coleccion ||
          idsIngresos.length === 0
        ) {
          return [];
        }


        const grupos =
          dividirArray(
            idsIngresos
          );


        const resultado = [];


        for (
          const grupo of grupos
        ) {

          const qBitacora =
            query(
              collection(
                db,
                coleccion
              ),

              where(
                "emp_id",
                "==",
                empidst
              ),

              where(
                "id_ingreso",
                "in",
                grupo
              )
            );


          const snap =
            await getDocs(
              qBitacora
            );


          snap.docs.forEach(
            (d) => {

              resultado.push({
                id: d.id,
                ...d.data(),
              });

            }
          );
        }


        return resultado;

      },
      [
        empidst,
      ]
    );


  // ====================================================
  // CARGAR SERVICIOS CONTRACTUALES DEL HOMECARE
  // ====================================================

  const cargarServicios =
    useCallback(
      async () => {

        if (
          !users?.emp_id ||
          !empidst
        ) {

          setRegistros([]);
          return;
        }


        // ==============================================
        // 1. ÓRDENES ST ORIGINADAS DESDE ESTE HOMECARE
        // ==============================================

        const qIngresos =
          query(
            collection(
              db,
              "ingresostcab"
            ),

            where(
              "emp_id",
              "==",
              empidst
            ),

            where(
              "emp_id_origen",
              "==",
              users.emp_id
            ),

            where(
              "origen_ingreso",
              "==",
              "TRASPASO_ST_INTERNO"
            )
          );


        const snapIngresos =
          await getDocs(
            qIngresos
          );


        const ingresos =
          snapIngresos.docs.map(
            (d) => ({
              id: d.id,
              ...d.data(),
            })
          );


        // ==============================================
        // 2. SOLO SERVICIOS CONTRACTUALES QUE
        //    GENERAN CERTIFICADO
        // ==============================================

        const servicios =
          ingresos.filter(
            (item) =>
              item.servicio ===
                "MANTENCION PREVENTIVA" ||
              item.servicio ===
                "PRESUPUESTO"
          );


        if (
          servicios.length === 0
        ) {

          setRegistros([]);
          return;
        }


        // ==============================================
        // 3. SEPARAR PREVENTIVAS Y CORRECTIVAS
        // ==============================================

        const preventivas =
          servicios.filter(
            (item) =>
              item.servicio ===
              "MANTENCION PREVENTIVA"
          );


        const correctivas =
          servicios.filter(
            (item) =>
              item.servicio ===
              "PRESUPUESTO"
          );


        const idsPreventivas =
          preventivas
            .map(
              (item) =>
                item.id
            )
            .filter(Boolean);


        const idsCorrectivas =
          correctivas
            .map(
              (item) =>
                item.id
            )
            .filter(Boolean);


        // ==============================================
        // 4. CABECERAS BITÁCORA PREVENTIVA
        //    bitacoracabst
        //
        //    CABECERAS BITÁCORA CORRECTIVA
        //    bitacoracabpresu
        // ==============================================

        const [
          bitacorasPrev,
          bitacorasCorr,
        ] =
          await Promise.all([

            buscarBitacorasCab({
              coleccion:
                "bitacoracabst",

              idsIngresos:
                idsPreventivas,
            }),

            buscarBitacorasCab({
              coleccion:
                "bitacoracabpresu",

              idsIngresos:
                idsCorrectivas,
            }),

          ]);


        const mapaPrev =
          crearMapaBitacoras(
            bitacorasPrev
          );


        const mapaCorr =
          crearMapaBitacoras(
            bitacorasCorr
          );


        // ==============================================
        // 5. ARMAR RESULTADO
        // ==============================================

        const resultado =
          servicios
            .map(
              (ingreso) => {

                const esCorrectiva =
                  ingreso.servicio ===
                  "PRESUPUESTO";


                const bitacora =
                  esCorrectiva
                    ? mapaCorr.get(
                        ingreso.id
                      )
                    : mapaPrev.get(
                        ingreso.id
                      );


                const tipoAtencion =
                  esCorrectiva
                    ? "CORRECTIVA"
                    : "PREVENTIVA";


                return {

                  ...ingreso,

                  id_ingreso_st:
                    ingreso.id,

                  tipo_atencion_reporte:
                    tipoAtencion,

                  tipo_certificado:
                    tipoAtencion,

                  id_bitacora_st:
                    bitacora?.id ||
                    "",

                  bitacora_confirmada:
                    bitacora?.confirmado ===
                    true,

                  observacion_bitacora:
                    bitacora
                      ?.observaciones ||
                    "",

                  fecha_bitacora:
                    bitacora?.fechamod ||
                    bitacora?.fechaadd ||
                    null,

                  tecnico_bitacora:
                    bitacora?.useradd ||
                    ingreso.tecnico ||
                    "",

                  coleccion_bitacora:
                    esCorrectiva
                      ? "bitacoracabpresu"
                      : "bitacoracabst",
                };

              }
            )

            // Solo mostramos servicios que ya poseen
            // certificado/bitácora.
            .filter(
              (item) =>
                Boolean(
                  item.id_bitacora_st
                )
            )

            .sort(
              (a, b) => {

                const fechaA =
                  obtenerMillis(
                    a.fecha_out ||
                    a.fecha_bitacora ||
                    a.fechamod
                  );


                const fechaB =
                  obtenerMillis(
                    b.fecha_out ||
                    b.fecha_bitacora ||
                    b.fechamod
                  );


                return (
                  fechaB -
                  fechaA
                );

              }
            );


        setRegistros(
          resultado
        );

      },
      [
        users?.emp_id,
        empidst,
        buscarBitacorasCab,
        crearMapaBitacoras,
      ]
    );


  // ====================================================
  // CARGA INICIAL
  // ====================================================

  useEffect(() => {

    let activo = true;


    const init =
      async () => {

        if (
          !users?.emp_id
        ) {
          return;
        }


        setLoading(true);
        setErrorCarga("");


        try {

          await Promise.all([
            cargarServicios(),
            cargarUsuariosST(),
          ]);

        } catch (error) {

          console.error(
            "Error cargando servicios por contrato:",
            error
          );


          if (activo) {

            setRegistros([]);

            setErrorCarga(
              "No fue posible cargar el historial de servicios por contrato. Intente nuevamente."
            );
          }

        } finally {

          if (activo) {
            setLoading(false);
          }

        }

      };


    init();


    return () => {
      activo = false;
    };

  }, [
    users?.emp_id,
    cargarServicios,
    cargarUsuariosST,
  ]);


  // ====================================================
  // FILTRO TIPO ATENCIÓN
  // ====================================================

  const registrosPorTipo =
    useMemo(
      () => {

        if (
          filtroAtencion ===
          "TODOS"
        ) {
          return registros;
        }


        return registros.filter(
          (item) =>
            item.tipo_atencion_reporte ===
            filtroAtencion
        );

      },
      [
        registros,
        filtroAtencion,
      ]
    );


  // ====================================================
  // BUSCADOR
  // ====================================================

  const filteredData =
    useMemo(
      () => {

        const texto =
          buscador
            .trim()
            .toLowerCase();


        if (!texto) {
          return registrosPorTipo;
        }


        return registrosPorTipo.filter(
          (item) => {

            const campos = [

              item.folio,

              item.paciente,

              item.entidad,

              item.rut,

              item.rut_paciente,

              item.familia,

              item.tipo,

              item.marca,

              item.modelo,

              item.serie,

              item.estado,

              item.estado_servicio,

              item.servicio,

              item.tipo_atencion_reporte,

              item.tecnico,

              formatearFecha(
                item.fecha_out
              ),

            ];


            return campos.some(
              (value) =>
                safeText(value)
                  .toLowerCase()
                  .includes(texto)
            );

          }
        );

      },
      [
        registrosPorTipo,
        buscador,
      ]
    );


  // ====================================================
  // KPIs
  // ====================================================

  const kpis =
    useMemo(
      () => {

        const total =
          registros.length;


        const preventivas =
          registros.filter(
            (item) =>
              item.tipo_atencion_reporte ===
              "PREVENTIVA"
          ).length;


        const correctivas =
          registros.filter(
            (item) =>
              item.tipo_atencion_reporte ===
              "CORRECTIVA"
          ).length;


        const entregadas =
          registros.filter(
            (item) =>
              item.estado ===
              "ENTREGADO"
          ).length;


        return {
          total,
          preventivas,
          correctivas,
          entregadas,
        };

      },
      [
        registros,
      ]
    );


  // ====================================================
  // BUSCAR TÉCNICO
  // ====================================================

  const asignarTecnicoPdf =
    useCallback(
      (
        item
      ) => {

        const correo =
          (
            item.tecnico_bitacora ||
            item.tecnico ||
            ""
          )
            .trim()
            .toLowerCase();


        if (!correo) {

          // Evita error de usuario[0]
          // en PDFs antiguos.
          setUserAdd([
            {
              nombre: "",
              apellido: "",
            },
          ]);

          return;
        }


        const tecnico =
          usuariosST.find(
            (usuario) =>
              (
                usuario.correo ||
                ""
              )
                .trim()
                .toLowerCase() ===
              correo
          );


        if (tecnico) {

          setUserAdd([
            tecnico,
          ]);

        } else {

          // Fallback para que un PDF antiguo
          // que use usuario[0] no se caiga.
          setUserAdd([
            {
              nombre: "",
              apellido: "",
            },
          ]);
        }

      },
      [
        usuariosST,
      ]
    );


  // ====================================================
  // ABRIR PDF PREVENTIVA
  // ====================================================

  const prepararPreventiva =
    useCallback(
      async (
        item
      ) => {

        const cabId =
          item.id_bitacora_st;


        // ============================================
        // DETALLE BITÁCORA PREVENTIVA
        // ============================================

        const qDetalles =
          query(
            collection(
              db,
              "bitacorasst"
            ),

            where(
              "emp_id",
              "==",
              empidst
            ),

            where(
              "cab_id_bitacora",
              "==",
              cabId
            )
          );


        const snapDetalles =
          await getDocs(
            qDetalles
          );


        const detalles =
          snapDetalles.docs.map(
            (d) => ({
              id: d.id,
              ...d.data(),
            })
          );


        const ordenar =
          (arr) =>
            [...arr].sort(
              (a, b) =>
                obtenerMillis(
                  a?.fechaadd
                ) -
                obtenerMillis(
                  b?.fechaadd
                )
            );


        setItemsInst(
          ordenar(
            detalles.filter(
              (itemDetalle) =>
                itemDetalle.categoria ===
                  "INSTRUMENTOS" ||
                itemDetalle.categoria ===
                  "INSTRUMENTO"
            )
          )
        );


        setItemsCheck(
          ordenar(
            detalles
              .filter(
                (itemDetalle) =>
                  itemDetalle.categoria ===
                  "CHECK"
              )
              .map(
                (itemDetalle, index) => ({
                  ...itemDetalle,
                  id2: index + 1,
                })
              )
          )
        );


        setItemsMed(
          ordenar(
            detalles
              .filter(
                (itemDetalle) =>
                  itemDetalle.categoria ===
                  "MEDICION"
              )
              .map(
                (itemDetalle, index) => ({
                  ...itemDetalle,
                  id2: index + 1,
                })
              )
          )
        );


        setItemsSeg(
          ordenar(
            detalles
              .filter(
                (itemDetalle) =>
                  itemDetalle.categoria ===
                  "SEGURIDAD"
              )
              .map(
                (itemDetalle, index) => ({
                  ...itemDetalle,
                  id2: index + 1,
                })
              )
          )
        );

      },
      [
        empidst,
      ]
    );


  // ====================================================
  // ABRIR PDF CORRECTIVA
  // ====================================================

  const prepararCorrectiva =
    useCallback(
      async (
        item
      ) => {

        // ============================================
        // 1. PRESUPUESTO CABECERA
        // ============================================

        const qPresuCab =
          query(
            collection(
              db,
              "presupuestoscab"
            ),

            where(
              "emp_id",
              "==",
              empidst
            ),

            where(
              "id_cab_inst",
              "==",
              item.id_ingreso_st
            )
          );


        const snapPresuCab =
          await getDocs(
            qPresuCab
          );


        const presuCabDoc =
          snapPresuCab.docs?.[0];


        // ============================================
        // 2. DETALLE PRESUPUESTO
        // ============================================

        if (presuCabDoc) {

          const qPresu =
            query(
              collection(
                db,
                "presupuestos"
              ),

              where(
                "emp_id",
                "==",
                empidst
              ),

              where(
                "id_cab_pre",
                "==",
                presuCabDoc.id
              )
            );


          const snapPresu =
            await getDocs(
              qPresu
            );


          const detallePresupuesto =
            snapPresu.docs.map(
              (d) => ({
                id: d.id,
                ...d.data(),
              })
            );


          const itemsPresupuesto =
            detallePresupuesto.filter(
              (d) =>
                d.categoria !==
                "FALLA"
            );


          const fallasPresupuesto =
            detallePresupuesto.filter(
              (d) =>
                d.categoria ===
                "FALLA"
            );


          const totalPresupuesto =
            detallePresupuesto
              .filter(
                (d) =>
                  d.categoria ===
                    "SERVICIO" ||
                  d.categoria ===
                    "REPUESTO"
              )
              .reduce(
                (
                  acc,
                  d
                ) =>
                  acc +
                  Number(
                    d.price ||
                    0
                  ),
                0
              );


          setPresu(
            itemsPresupuesto
          );


          setFalla(
            fallasPresupuesto
          );


          setTotal(
            totalPresupuesto
          );

        } else {

          setPresu([]);
          setFalla([]);
          setTotal(0);

        }


        // ============================================
        // 3. DETALLE BITÁCORA CORRECTIVA
        // ============================================

        const qBitacora =
          query(
            collection(
              db,
              "bitacorapresu"
            ),

            where(
              "emp_id",
              "==",
              empidst
            ),

            where(
              "id_cab_pre",
              "==",
              item.id_bitacora_st
            )
          );


        const snapBitacora =
          await getDocs(
            qBitacora
          );


        const detalles =
          snapBitacora.docs.map(
            (d) => ({
              id: d.id,
              ...d.data(),
            })
          );


        const ordenar =
          (arr) =>
            [...arr].sort(
              (a, b) =>
                obtenerMillis(
                  a?.fechaadd
                ) -
                obtenerMillis(
                  b?.fechaadd
                )
            );


        // En bitacorapresu actualmente
        // se usa INSTRUMENTO singular.
        setItemsInst(
          ordenar(
            detalles.filter(
              (d) =>
                d.categoria ===
                  "INSTRUMENTO" ||
                d.categoria ===
                  "INSTRUMENTOS"
            )
          )
        );


        setItemsCheck(
          ordenar(
            detalles.filter(
              (d) =>
                d.categoria ===
                "CHECK"
            )
          )
        );


        setItemsMed(
          ordenar(
            detalles.filter(
              (d) =>
                d.categoria ===
                "MEDICION"
            )
          )
        );


        setItemsSeg(
          ordenar(
            detalles.filter(
              (d) =>
                d.categoria ===
                "SEGURIDAD"
            )
          )
        );

      },
      [
        empidst,
      ]
    );


  // ====================================================
  // ABRIR CERTIFICADO
  // ====================================================

  const pdfver =
    useCallback(
      async (
        item
      ) => {

        if (
          !empidst ||
          !item?.id_ingreso_st ||
          !item?.id_bitacora_st
        ) {
          return;
        }


        limpiarDatosPdf();

        setPdfLoading(true);
        setOpenPdf(true);


        const esCorrectiva =
          item.tipo_atencion_reporte ===
          "CORRECTIVA";


        setTipoPdf(
          esCorrectiva
            ? "CORRECTIVA"
            : "PREVENTIVA"
        );


        try {

          // ============================================
          // CABECERA COMÚN
          // ============================================

          setCabecera([
            item,
          ]);


          setObs(
            item
              .observacion_bitacora ||
            ""
          );


          asignarTecnicoPdf(
            item
          );


          // ============================================
          // PREVENTIVA
          // ============================================

          if (!esCorrectiva) {

            await prepararPreventiva(
              item
            );

            return;
          }


          // ============================================
          // CORRECTIVA
          // ============================================

          await prepararCorrectiva(
            item
          );


        } catch (error) {

          console.error(
            "Error preparando certificado:",
            error
          );

        } finally {

          setPdfLoading(false);

        }

      },
      [
        empidst,
        limpiarDatosPdf,
        asignarTecnicoPdf,
        prepararPreventiva,
        prepararCorrectiva,
      ]
    );


  // ====================================================
  // CERRAR PDF
  // ====================================================

  const cerrarPdf =
    useCallback(() => {

      setOpenPdf(false);

      setPdfLoading(false);

      limpiarDatosPdf();

    }, [
      limpiarDatosPdf,
    ]);


  // ====================================================
  // RETURN
  // ====================================================

  return (

    <div
      style={{
        minHeight: "100vh",
        background: COLORS.background,
        padding: "24px 18px 40px",
      }}
    >

      <div
        style={{
          width: "100%",
          maxWidth: 1400,
          margin: "0 auto",
        }}
      >

        {/* =================================================
            HEADER
        ================================================= */}

        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 16,
            marginBottom: 20,
            flexWrap: "wrap",
          }}
        >

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 14,
            }}
          >

            <div
              style={{
                width: 48,
                height: 48,
                borderRadius: 9,
                background: COLORS.tealSoft,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >

              <Icon
                name="clipboard check"
                size="large"
                style={{
                  color: COLORS.teal,
                  margin: 0,
                }}
              />

            </div>


            <div>

              <div
                style={{
                  color: COLORS.title,
                  fontSize: 22,
                  fontWeight: 700,
                  lineHeight: 1.2,
                }}
              >
                Servicios por Contrato
              </div>


              <div
                style={{
                  color: COLORS.subtitle,
                  fontSize: 13,
                  marginTop: 4,
                }}
              >
                Certificados de mantenciones preventivas
                y reparaciones correctivas realizadas
                por Servicio Técnico
              </div>

            </div>

          </div>

        </div>


        {/* =================================================
            KPIs
        ================================================= */}

        <div
          style={{
            display: "grid",
            gridTemplateColumns:
              "repeat(auto-fit, minmax(190px, 1fr))",
            gap: 14,
            marginBottom: 18,
          }}
        >

          {/* TOTAL */}

          <div
            style={{
              ...cardStyle,
              padding: "16px 18px",
              display: "flex",
              alignItems: "center",
              gap: 14,
            }}
          >

            <div
              style={{
                width: 42,
                height: 42,
                borderRadius: 8,
                background: COLORS.tealSoft,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >

              <Icon
                name="clipboard list"
                style={{
                  color: COLORS.teal,
                  margin: 0,
                }}
              />

            </div>


            <div>

              <div
                style={{
                  fontSize: 12,
                  color: COLORS.subtitle,
                  fontWeight: 600,
                }}
              >
                Servicios
              </div>

              <div
                style={{
                  fontSize: 23,
                  lineHeight: 1.2,
                  fontWeight: 700,
                  color: COLORS.title,
                }}
              >
                {kpis.total}
              </div>

            </div>

          </div>


          {/* PREVENTIVAS */}

          <div
            style={{
              ...cardStyle,
              padding: "16px 18px",
              display: "flex",
              alignItems: "center",
              gap: 14,
            }}
          >

            <div
              style={{
                width: 42,
                height: 42,
                borderRadius: 8,
                background: COLORS.tealSoft,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >

              <Icon
                name="calendar check outline"
                style={{
                  color: COLORS.teal,
                  margin: 0,
                }}
              />

            </div>


            <div>

              <div
                style={{
                  fontSize: 12,
                  color: COLORS.subtitle,
                  fontWeight: 600,
                }}
              >
                Preventivas
              </div>

              <div
                style={{
                  fontSize: 23,
                  lineHeight: 1.2,
                  fontWeight: 700,
                  color: COLORS.title,
                }}
              >
                {kpis.preventivas}
              </div>

            </div>

          </div>


          {/* CORRECTIVAS */}

          <div
            style={{
              ...cardStyle,
              padding: "16px 18px",
              display: "flex",
              alignItems: "center",
              gap: 14,
            }}
          >

            <div
              style={{
                width: 42,
                height: 42,
                borderRadius: 8,
                background: COLORS.orangeSoft,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >

              <Icon
                name="wrench"
                style={{
                  color: COLORS.orange,
                  margin: 0,
                }}
              />

            </div>


            <div>

              <div
                style={{
                  fontSize: 12,
                  color: COLORS.subtitle,
                  fontWeight: 600,
                }}
              >
                Correctivas
              </div>

              <div
                style={{
                  fontSize: 23,
                  lineHeight: 1.2,
                  fontWeight: 700,
                  color: COLORS.title,
                }}
              >
                {kpis.correctivas}
              </div>

            </div>

          </div>


          {/* ENTREGADAS */}

          <div
            style={{
              ...cardStyle,
              padding: "16px 18px",
              display: "flex",
              alignItems: "center",
              gap: 14,
            }}
          >

            <div
              style={{
                width: 42,
                height: 42,
                borderRadius: 8,
                background: COLORS.greenSoft,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >

              <Icon
                name="check circle outline"
                style={{
                  color: COLORS.green,
                  margin: 0,
                }}
              />

            </div>


            <div>

              <div
                style={{
                  fontSize: 12,
                  color: COLORS.subtitle,
                  fontWeight: 600,
                }}
              >
                Entregadas
              </div>

              <div
                style={{
                  fontSize: 23,
                  lineHeight: 1.2,
                  fontWeight: 700,
                  color: COLORS.title,
                }}
              >
                {kpis.entregadas}
              </div>

            </div>

          </div>

        </div>


        {/* =================================================
            TABLA
        ================================================= */}

        <Segment
          style={{
            ...cardStyle,
            padding: 0,
            margin: 0,
            overflow: "hidden",
          }}
        >

          {/* HEADER TABLA */}

          <div
            style={{
              minHeight: 64,
              padding: "12px 16px",
              borderBottom:
                `1px solid ${COLORS.border}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 14,
              flexWrap: "wrap",
            }}
          >

            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 9,
              }}
            >

              <Icon
                name="history"
                style={{
                  color: COLORS.teal,
                  margin: 0,
                }}
              />


              <div>

                <div
                  style={{
                    color: COLORS.title,
                    fontSize: 14,
                    fontWeight: 700,
                  }}
                >
                  Historial de Servicios
                </div>


                <div
                  style={{
                    color: COLORS.subtitle,
                    fontSize: 11,
                    marginTop: 2,
                  }}
                >
                  {filteredData.length} registro
                  {filteredData.length === 1
                    ? ""
                    : "s"}
                </div>

              </div>

            </div>


            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                flexWrap: "wrap",
              }}
            >

              {/* FILTROS */}

              <Button.Group size="small">

                <Button
                  basic={
                    filtroAtencion !==
                    "TODOS"
                  }
                  color="teal"
                  active={
                    filtroAtencion ===
                    "TODOS"
                  }
                  onClick={() =>
                    setFiltroAtencion(
                      "TODOS"
                    )
                  }
                >
                  Todos
                </Button>


                <Button
                  basic={
                    filtroAtencion !==
                    "PREVENTIVA"
                  }
                  color="teal"
                  active={
                    filtroAtencion ===
                    "PREVENTIVA"
                  }
                  onClick={() =>
                    setFiltroAtencion(
                      "PREVENTIVA"
                    )
                  }
                >
                  Preventivas
                </Button>


                <Button
                  basic={
                    filtroAtencion !==
                    "CORRECTIVA"
                  }
                  color="orange"
                  active={
                    filtroAtencion ===
                    "CORRECTIVA"
                  }
                  onClick={() =>
                    setFiltroAtencion(
                      "CORRECTIVA"
                    )
                  }
                >
                  Correctivas
                </Button>

              </Button.Group>


              <Input
                size="small"
                icon={
                  buscador.trim()
                    ? {
                        name: "close",
                        link: true,
                        onClick: () =>
                          setBuscador(""),
                      }
                    : "search"
                }
                placeholder="Buscar paciente, equipo, serie, folio..."
                value={buscador}
                onChange={(e) =>
                  setBuscador(
                    e.target.value
                  )
                }
                style={{
                  width: 320,
                  maxWidth: "100%",
                }}
              />

            </div>

          </div>


          {/* ERROR */}

          {errorCarga && (

            <div
              style={{
                padding: 16,
              }}
            >

              <Message
                negative
                icon
              >

                <Icon name="warning circle" />

                <Message.Content>

                  <Message.Header>
                    Error al cargar
                  </Message.Header>

                  {errorCarga}

                </Message.Content>

              </Message>

            </div>

          )}


          {/* LOADING */}

          {loading ? (

            <div
              style={{
                height: 260,
                position: "relative",
              }}
            >

              <Dimmer
                active
                inverted
              >

                <Loader
                  size="medium"
                >
                  Cargando servicios...
                </Loader>

              </Dimmer>

            </div>

          ) : !errorCarga &&
              filteredData.length === 0 ? (

            // EMPTY STATE

            <div
              style={{
                minHeight: 260,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 30,
                textAlign: "center",
              }}
            >

              <div>

                <Icon
                  name="clipboard outline"
                  size="huge"
                  style={{
                    color: "#c1c7d0",
                    marginBottom: 12,
                  }}
                />


                <Header
                  as="h4"
                  style={{
                    color: COLORS.title,
                    marginBottom: 6,
                  }}
                >
                  Sin servicios
                </Header>


                <div
                  style={{
                    color: COLORS.subtitle,
                    fontSize: 13,
                  }}
                >

                  {buscador.trim()
                    ? "No existen registros que coincidan con la búsqueda."
                    : "No existen servicios contractuales con certificado asociado."}

                </div>

              </div>

            </div>

          ) : !errorCarga ? (

            // TABLA

            <div
              style={{
                overflowX: "auto",
                overflowY: "auto",
                maxHeight: 580,
              }}
            >

              <Table
                celled
                compact
                selectable
                unstackable
                style={{
                  margin: 0,
                  border: "none",
                  minWidth: 1150,
                }}
              >

                <Table.Header>

                  <Table.Row>

                    <Table.HeaderCell
                      style={{
                        ...stickyTh,
                        width: 55,
                        textAlign: "center",
                      }}
                    >
                      #
                    </Table.HeaderCell>


                    <Table.HeaderCell
                      style={stickyTh}
                    >
                      Orden ST
                    </Table.HeaderCell>


                    <Table.HeaderCell
                      style={{
                        ...stickyTh,
                        width: 120,
                        textAlign: "center",
                      }}
                    >
                      Atención
                    </Table.HeaderCell>


                    <Table.HeaderCell
                      style={stickyTh}
                    >
                      Paciente
                    </Table.HeaderCell>


                    <Table.HeaderCell
                      style={stickyTh}
                    >
                      Equipo
                    </Table.HeaderCell>


                    <Table.HeaderCell
                      style={stickyTh}
                    >
                      Modelo
                    </Table.HeaderCell>


                    <Table.HeaderCell
                      style={stickyTh}
                    >
                      Serie
                    </Table.HeaderCell>


                    <Table.HeaderCell
                      style={stickyTh}
                    >
                      Fecha
                    </Table.HeaderCell>


                    <Table.HeaderCell
                      style={stickyTh}
                    >
                      Estado
                    </Table.HeaderCell>


                    <Table.HeaderCell
                      style={{
                        ...stickyTh,
                        width: 125,
                        textAlign: "center",
                      }}
                    >
                      Certificado
                    </Table.HeaderCell>

                  </Table.Row>

                </Table.Header>


                <Table.Body>

                  {filteredData.map(
                    (
                      item,
                      index
                    ) => (

                      <Table.Row
                        key={
                          item.id_ingreso_st
                        }
                      >

                        {/* N° */}

                        <Table.Cell
                          textAlign="center"
                          style={
                            normalCellStyle
                          }
                        >
                          {index + 1}
                        </Table.Cell>


                        {/* ORDEN */}

                        <Table.Cell
                          style={{
                            ...normalCellStyle,
                            fontWeight: 600,
                            color: COLORS.blue,
                          }}
                        >
                          {item.folio || "-"}
                        </Table.Cell>


                        {/* ATENCION */}

                        <Table.Cell
                          textAlign="center"
                        >

                          <Label
                            size="tiny"
                            color={
                              item
                                .tipo_atencion_reporte ===
                              "CORRECTIVA"
                                ? "orange"
                                : "teal"
                            }
                          >

                            <Icon
                              name={
                                item
                                  .tipo_atencion_reporte ===
                                "CORRECTIVA"
                                  ? "wrench"
                                  : "calendar check"
                              }
                            />

                            {
                              item
                                .tipo_atencion_reporte
                            }

                          </Label>

                        </Table.Cell>


                        {/* PACIENTE */}

                        <Table.Cell
                          style={{
                            ...normalCellStyle,
                            whiteSpace: "normal",
                            minWidth: 170,
                          }}
                        >
                          {item.paciente ||
                            item.entidad ||
                            "-"}
                        </Table.Cell>


                        {/* EQUIPO */}

                        <Table.Cell
                          style={
                            normalCellStyle
                          }
                        >
                          {item.tipo || "-"}
                        </Table.Cell>


                        {/* MODELO */}

                        <Table.Cell
                          style={
                            normalCellStyle
                          }
                        >
                          {item.modelo || "-"}
                        </Table.Cell>


                        {/* SERIE */}

                        <Table.Cell
                          style={{
                            ...normalCellStyle,
                            fontFamily:
                              "monospace",
                          }}
                        >
                          {item.serie || "-"}
                        </Table.Cell>


                        {/* FECHA */}

                        <Table.Cell
                          style={{
                            ...normalCellStyle,
                            whiteSpace: "nowrap",
                          }}
                        >

                          {formatearFecha(
                            item.fecha_out ||
                            item.fecha_bitacora
                          )}

                        </Table.Cell>


                        {/* ESTADO */}

                        <Table.Cell>

                          <span
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 6,
                              padding: "4px 8px",
                              borderRadius: 999,
                              fontSize: 11,
                              fontWeight: 700,

                              background:
                                item.estado ===
                                "ENTREGADO"
                                  ? COLORS.greenSoft
                                  : COLORS.blueSoft,

                              color:
                                item.estado ===
                                "ENTREGADO"
                                  ? COLORS.green
                                  : COLORS.blue,
                            }}
                          >

                            <Icon
                              name={
                                item.estado ===
                                "ENTREGADO"
                                  ? "check circle"
                                  : "wrench"
                              }
                              fitted
                            />

                            {item.estado || "-"}

                          </span>

                        </Table.Cell>


                        {/* PDF */}

                        <Table.Cell
                          textAlign="center"
                        >

                          <Button
                            basic
                            size="tiny"
                            icon
                            title={
                              item
                                .tipo_atencion_reporte ===
                              "CORRECTIVA"
                                ? "Ver certificado de reparación"
                                : "Ver certificado de mantención"
                            }
                            loading={
                              pdfLoading &&
                              cabecera?.[0]
                                ?.id_ingreso_st ===
                                item.id_ingreso_st
                            }
                            disabled={
                              pdfLoading
                            }
                            onClick={() =>
                              pdfver(item)
                            }
                            style={{
                              color: COLORS.red,
                              borderColor:
                                "#f3c2c0",
                              boxShadow: "none",
                            }}
                          >

                            <Icon
                              name="file pdf outline"
                              style={{
                                margin: 0,
                                color: COLORS.red,      
                              }}
                            />

                          </Button>

                        </Table.Cell>

                      </Table.Row>

                    )
                  )}

                </Table.Body>

              </Table>

            </div>

          ) : null}


          {/* FOOTER */}

          {!loading &&
            !errorCarga &&
            registros.length > 0 && (

              <div
                style={{
                  minHeight: 42,
                  padding: "10px 16px",
                  borderTop:
                    `1px solid ${COLORS.border}`,
                  background: "#fafbfc",
                  display: "flex",
                  alignItems: "center",
                  justifyContent:
                    "space-between",
                  gap: 12,
                  flexWrap: "wrap",
                  color: COLORS.subtitle,
                  fontSize: 11,
                }}
              >

                <span>
                  Mostrando{" "}
                  <strong>
                    {filteredData.length}
                  </strong>{" "}
                  de{" "}
                  <strong>
                    {registros.length}
                  </strong>{" "}
                  servicios
                </span>


                {(buscador.trim() ||
                  filtroAtencion !==
                    "TODOS") && (

                  <Button
                    basic
                    size="mini"
                    onClick={() => {

                      setBuscador("");

                      setFiltroAtencion(
                        "TODOS"
                      );

                    }}
                  >
                    <Icon name="eraser" />
                    Limpiar filtros
                  </Button>

                )}

              </div>

            )}

        </Segment>


        {/* =================================================
            MODAL PDF
        ================================================= */}

        <Modal
          open={openPdf}
          onClose={cerrarPdf}
          size="large"
          closeIcon
          closeOnEscape={
            !pdfLoading
          }
          closeOnDimmerClick={
            !pdfLoading
          }
        >

          <Modal.Header
            style={{
              padding: "14px 18px",
              color: COLORS.title,
              fontSize: 16,
              borderBottom:
                `1px solid ${COLORS.border}`,
            }}
          >

            <Icon
              name="file pdf outline"
              style={{
                color: COLORS.red,
              }}
            />


            {tipoPdf ===
            "CORRECTIVA"
              ? "Certificado Técnico de Reparación"
              : "Certificado de Mantención Preventiva"}

          </Modal.Header>


          <Modal.Content
            style={{
              padding: 0,
              background: "#f4f5f7",
            }}
          >

            {pdfLoading ? (

              <div
                style={{
                  height: 580,
                  position: "relative",
                }}
              >

                <Dimmer
                  active
                  inverted
                >

                  <Loader size="large">
                    Preparando certificado...
                  </Loader>

                </Dimmer>

              </div>

            ) : (

              <PDFViewer
                width="100%"
                height="580"
                style={{
                  border: "none",
                  display: "block",
                }}
              >

                {tipoPdf ===
                "CORRECTIVA" ? (

                  // =====================================
                  // CERTIFICADO CORRECTIVA
                  // =====================================

                  <ReporteFinalPresuPDF
                    cabecera={
                      cabecera
                    }

                    falla={
                      falla
                    }

                    presu={
                      presu
                    }

                    total={
                      total
                    }

                    obs={
                      obs
                    }

                    instrumento={
                      itemsInst
                    }

                    check={
                      itemsCheck
                    }

                    medicion={
                      itemsMed
                    }

                    seguridad={
                      itemsSeg
                    }

                    usuario={
                      userAdd
                    }

                    empresa={
                      empidst
                    }
                  />

                ) : (

                  // =====================================
                  // CERTIFICADO PREVENTIVA
                  // =====================================

                  <ReporteMantePrevPDF
                    cabecera={
                      cabecera
                    }

                    itemsInst={
                      itemsInst
                    }

                    itemsCheck={
                      itemsCheck
                    }

                    itemsMed={
                      itemsMed
                    }

                    itemsSeg={
                      itemsSeg
                    }

                    usuario={
                      userAdd
                    }

                    empresa={
                      empidst
                    }

                    obs={
                      obs
                    }
                  />

                )}

              </PDFViewer>

            )}

          </Modal.Content>


          <Modal.Actions
            style={{
              padding: "10px 14px",
              borderTop:
                `1px solid ${COLORS.border}`,
            }}
          >

            <Button
              basic
              size="small"
              disabled={
                pdfLoading
              }
              onClick={
                cerrarPdf
              }
            >
              <Icon name="close" />
              Cerrar
            </Button>

          </Modal.Actions>

        </Modal>

      </div>

    </div>

  );
};


export default ReporteMantencionesContrato;